import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'

import VNConcessionsNavBar from '../VNConcessionsNavBar'
import Lock from '@material-ui/icons/Lock'
import styles from './Component.module.scss'
import VNLoader from '../../VNLoader/components/VNLoader'
import NavMenu from '../NavMenu'

import VNPinPad, { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '../../VNLogin/components/VNPinPad'
import { ToastManager } from '../../utils/toastManager'
import PeripheralBridge from '../../utils/peripheralBridge'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { useSelector } from 'react-redux'
import { getCameraPermissions, getVNScannerInitialized, getVNScannerResults } from '../../VNMobileScanner/Selectors'
import { MobileScanner } from '../../VNMobileScanner/components/MobileScanner'
import VNSubmitButton from '../../VNLogin/components/VNSubmitButton'
import { getBarcodeScanningSettings } from '../../selectors/config'

/**
 * UserLoginComponent - User/Manager Login Pin Entry Screen
 *
 * @param {Object} props - The properties object.
 * @returns {JSX.Element} The rendered component.
 */
const UserLoginComponent = ({
  standName,
  pinLoginStatus,
  handleSubmit,
  approvalCodes,
  hamburgerUnlocked,
  fetchAttendantsStatus,
  isMounted,
  header = 'Enter Your Passcode',
  buttonText = 'Sign In',
  employeeIds,
  forManagerApproval = false,
}) => {
  const headerScanEnabledText = forManagerApproval ? 'Type Manager Pin or Scan ID' : 'Type Pin or Scan ID'
  const [pin, setPin] = useState('')
  const pinRef = useRef(pin)
  const [scannerActive, setScannerActive] = useState(true)
  const scannerRef = useRef(true)
  const [openHamburgerMenu, setOpenHamburgerMenu] = useState(false)
  const isMobile = useGetIsMobileScreen()
  const mobileScanResults = useSelector((state) => getVNScannerResults(state))
  const isCameraPermissionAllowed = useSelector((state) => getCameraPermissions(state))
  const isMobileScannerInitialized = useSelector((state) => getVNScannerInitialized(state))
  const barcodeScanningSettings = useSelector(getBarcodeScanningSettings)
  const [mobileScannerEnabled, setMobileScannerEnabled] = useState(false)
  const SCANNER_ACTIVE_TIMEOUT = 2300

  const isLoading =
    !forManagerApproval &&
    (['loading', 'succeeded'].includes(pinLoginStatus) || fetchAttendantsStatus === 'loading' || !isMounted)

  const stopScanner = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (barcodeScanningSettings.barcodeScanningEnabled) {
      return
    }
    bridgeInstance.call('stopScanner')
  }

  const isValid = useMemo(() => pin.length >= PIN_MIN_LENGTH && pin.length <= PIN_MAX_LENGTH, [pin])

  const handleScanError = useCallback((err) => {
    if (err && err.toLowerCase().includes('no scanner detected')) {
      ToastManager.error('Scanner not connected')
      setScannerActive(false)
    }
  }, [])

  const registerScanError = useCallback(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) {
      setScannerActive(false)
      return
    }

    bridgeInstance.registerHandler('scanError', handleScanError)
  }, [handleScanError])

  const startScanner = useCallback(
    (showToast = true) => {
      if (isMobile) {
        if (!navigator?.userAgent?.includes('A800')) {
          ToastManager.error('Scanner not supported')
          return
        }

        if (!isCameraPermissionAllowed) {
          ToastManager.error('You must grant camera permissions and relaunch the app to scan.')
          return
        }

        if (!window.VNMobileBarcodePicker || !isMobileScannerInitialized) {
          ToastManager.error('Scanner not initialized')
          return
        }

        setMobileScannerEnabled(true)
        return
      }
      const bridgeInstance = PeripheralBridge.getBridge()
      if (!bridgeInstance) return

      setTimeout(() => {
        if (scannerRef.current && showToast) {
          ToastManager.success('Scanner is ready')
        }
      }, SCANNER_ACTIVE_TIMEOUT)
      registerScanError()
      setScannerActive(true)
      bridgeInstance.call('startScanner')
    },
    [isCameraPermissionAllowed, isMobile, isMobileScannerInitialized, registerScanError],
  )

  useEffect(() => {
    if (!isMobile) {
      startScanner(false)
    }

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    bridgeInstance.registerHandler('scanPayload', function (data, callback) {
      const scannedMedium = data?.valueOf()
      onHandleSubmit(null, scannedMedium, true)
      // in the case that the scan code is incorrect, we want to show the user that we did something.
      setPin(scannedMedium)
    })

    bridgeInstance.registerHandler('scanError', () => {
      setScannerActive(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  useEffect(() => {
    pinRef.current = pin
  }, [pin])

  useEffect(() => {
    scannerRef.current = scannerActive
  }, [scannerActive])

  const onError = useCallback(() => {
    if (scannerActive) {
      startScanner()
    }
  }, [scannerActive, startScanner])

  // there are two variables because the VNPinPad component uses a button with onClick.
  // That would pass us a variable, so we have an extra one where it would not pass in a variable and mess with the logic of this function.
  const onHandleSubmit = useCallback(
    (_, _pin, isScan = false) => {
      const inputPin = _pin ? _pin : pinRef.current

      // pin can be the employee id if the user scanned a badge.
      if (employeeIds.includes(inputPin)) {
        stopScanner()
        handleSubmit(inputPin, isScan, onError)
        return
      }

      if (!approvalCodes?.includes(inputPin)) {
        ToastManager.error('That code did not work')
        return
      }

      stopScanner()
      handleSubmit(inputPin, isScan, onError)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [approvalCodes, employeeIds, handleSubmit, onError],
  )

  useEffect(() => {
    if (mobileScanResults && mobileScanResults?.get('results')) {
      onHandleSubmit(null, mobileScanResults?.get('results'), true)
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [mobileScanResults])

  const lockIcon = useMemo(() => <Lock className={styles.lock} />, [])

  // TODO(mkramerl): This needs to be refactored to use the singular loader.
  if (isLoading) {
    return <VNLoader />
  }

  if (mobileScannerEnabled) {
    return (
      <div className={cn(styles.container, { [styles.loading]: isLoading })}>
        <MobileScanner
          onKeyboardClick={() => {
            setMobileScannerEnabled(false)
          }}
          useHamburgerIcon={hamburgerUnlocked}
          leftAccessory={lockIcon}
          toolTipText={'Please scan ID'}
        />
      </div>
    )
  }

  return (
    <div className={cn(styles.container, { [styles.loading]: isLoading })}>
      <VNConcessionsNavBar
        useHamburgerIcon={hamburgerUnlocked}
        useSmallTitleFont={true}
        textDisplay={standName}
        showBorder={false}
        leftAccessory={lockIcon}
      />
      <div className={styles.pinPadContainer}>
        <VNPinPad pin={pin} pinRef={pinRef} onSetPin={setPin} startScanner={startScanner} header={headerScanEnabledText} />
        {openHamburgerMenu && <NavMenu onToggle={() => setOpenHamburgerMenu(false)} />}
        <VNSubmitButton disabled={!isValid} onClick={onHandleSubmit} text={buttonText} />
      </div>
    </div>
  )
}

export default UserLoginComponent
